import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import type { RefObject } from 'react';
import { useRouter } from 'next/router';
import { SectionHeader } from '../SectionHeader';
import {
  GetPashaFilterDataQuery,
  PashaItemsQuery
} from '../../../graphql/graphql';
import { getPashaItems } from '../../../api/queries/pasha';
import useScreenSize from '../../../hooks/useScreenSize';
import Pagination from '../../ui/Pagination';
import { SortOption } from '../../../graphql/types';
import { useOnScreen } from '../../../hooks/useOnScreen';
import {
  getCurrentSortOption,
  PASHA_LANDING_SLUG
} from '../../../utils/helpers/search';
import { FlexColumn } from '../../../styled/common';
import { FilterPanel } from './FilterPanel';
import { CardPlaceholder } from './CardPlaceholder';
import { FilterResult } from './FilterResult';
import { useClient } from '../../../hooks/useClient';

enum ItemCount {
  desk = 10,
  mob = 6
}

export const OffersSection: React.FC<{
  pashaItems?: PashaItemsQuery['items'];
  totalCount: number;
  setVisibleTabRef: React.Dispatch<
    React.SetStateAction<RefObject<HTMLDivElement> | null>
  >;
  setPashaItemsRef: React.Dispatch<
    React.SetStateAction<RefObject<HTMLDivElement> | null>
  >;
  sortOption: SortOption;
  currentFilterValues?: unknown;
  filtersParams?: GetPashaFilterDataQuery;
}> = ({
  sortOption,
  currentFilterValues,
  filtersParams,
  pashaItems,
  totalCount,
  setPashaItemsRef,
  setVisibleTabRef
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { isLarge } = useScreenSize();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isVisible = useOnScreen(wrapperRef);
  const isClient = useClient();
  const isLargeSafe = isClient && isLarge;

  const [filterValues, setFilterValues] = useState(currentFilterValues);
  const [currSortOption, setCurrSortOption] = useState<SortOption>(
    getCurrentSortOption(filtersParams, router?.query, sortOption)
  );

  const isPashaPage = router.pathname === '/pasha/[[...search]]';

  const [currentPage, setCurrentPage] = useState(1);
  const [currTotalCount, setCurrTotalCount] = useState(
    Math.ceil(totalCount / ItemCount.desk) || 0
  );

  const [fetchPashaItems, { loading, data }] = useLazyQuery(getPashaItems, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: res => {
      const limit = isLargeSafe ? ItemCount.desk : ItemCount.mob;
      const count = Math.ceil(res.itemsConnection?.totalCount / limit) || 0;
      if (count !== currTotalCount) {
        setCurrTotalCount(count);
      }
      if (currentPage > count) {
        setCurrentPage(count || 1);
      }
    }
  });

  useEffect(() => {
    setCurrSortOption(
      getCurrentSortOption(filtersParams, router?.query, sortOption)
    );
  }, [router.query.sorting]);

  useEffect(() => {
    const limit = isLargeSafe ? ItemCount.desk : ItemCount.mob;

    const safeFilterValues =
      filterValues && typeof filterValues === 'object'
        ? (filterValues as Record<string, unknown>)
        : {};

    const variables = {
      limit,
      sort: currSortOption.name,
      offset: (currentPage - 1) * limit,
      connectionSort: currSortOption.name,
      filter: { landingBrandSlug: PASHA_LANDING_SLUG, ...safeFilterValues }
    };
    fetchPashaItems({ variables });
  }, [
    currentPage,
    isLargeSafe,
    currTotalCount,
    filterValues,
    currSortOption,
    fetchPashaItems
  ]);

  useEffect(() => {
    setPashaItemsRef?.(wrapperRef);
    if (isVisible) return setVisibleTabRef?.(wrapperRef);
  }, [isVisible]);

  return (
    <FlexColumn ref={wrapperRef}>
      <SectionHeader title={t('pasha.offers_section.title')} />
      {filtersParams && (
        <FilterPanel
          {...{ filterValues, filtersParams, currSortOption, setFilterValues }}
        />
      )}
      {loading ? (
        <CardPlaceholder />
      ) : (
        <FilterResult pashaItems={data?.items || pashaItems || []} />
      )}
      {currTotalCount > 1 && (
        <Pagination
          totalPages={currTotalCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          isPashaLanding={isPashaPage}
        />
      )}
    </FlexColumn>
  );
};
